import { useMemo, useState } from 'react';
import { Network, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { SelectNativo } from '@/components/ui/select-nativo';
import { AvatarDePersona } from '@/components/ui/avatar-de-persona';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCarga } from '@/features/dynamic-pricing/monitoreo/hooks/use-carga';
import { organizationService } from '@/features/organizations/services/organization-service';
import { masterDataService } from '@/features/master-data/services/master-data-service';
import {
  atributosSoporteNoPuede,
  AVISO_SOPORTE_NO_PUEDE,
  useEnSoporte,
} from '@/features/mini-prixia/soporte-solo-mira';
import { toast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { MarcoDeMiEmpresa } from '../components/marco-de-mi-empresa';
import {
  miEmpresaService,
  type EquipoDelOrganigrama,
  type PersonaDeLaEmpresa,
} from '../services/mi-empresa-service';

type Persona = { id: string; firstName: string; lastName: string };

const nombreCompleto = (p: Persona) => `${p.firstName} ${p.lastName}`.trim();

/** «Gabriela Villca» → «G. Villca»; un nombre suelto («malba») queda igual. */
function nombreCorto(p: Persona): string {
  const ap = p.lastName.trim();
  return ap ? `${p.firstName.trim()[0]}. ${ap.split(/\s+/)[0]}` : p.firstName;
}

/** El renglón chico de cada tarjeta (maqueta): «Hardware · G. Villca» o «malba · líder». */
function subtituloDelEquipo(e: EquipoDelOrganigrama): string {
  if (e.descripcion && e.lider) return `${e.descripcion} · ${nombreCorto(e.lider)}`;
  if (e.lider) return `${nombreCompleto(e.lider)} · líder`;
  return e.descripcion ?? 'Sin líder';
}

/** El líder primero y después el resto, sin repetir a nadie. */
function genteDelEquipo(e: EquipoDelOrganigrama): Persona[] {
  const vistos = new Set<string>();
  return [...(e.lider ? [e.lider] : []), ...e.miembros].filter((p) =>
    vistos.has(p.id) ? false : (vistos.add(p.id), true)
  );
}

/**
 * Los equipos como árbol. Un equipo cuyo superior ya no existe (se borró) sube
 * a la raíz en vez de desaparecer.
 */
export function armarArbol(equipos: EquipoDelOrganigrama[]) {
  const ids = new Set(equipos.map((e) => e.id));
  const hijos = new Map<string | null, EquipoDelOrganigrama[]>();
  for (const e of equipos) {
    const padre = e.parentId && ids.has(e.parentId) ? e.parentId : null;
    hijos.set(padre, [...(hijos.get(padre) ?? []), e]);
  }
  return (id: string | null) => hijos.get(id) ?? [];
}

/** Los ids de un equipo y de todos los que cuelgan de él: no pueden ser su superior. */
function descendientes(id: string, hijosDe: (id: string | null) => EquipoDelOrganigrama[]) {
  const fuera = new Set<string>([id]);
  const pila = [id];
  while (pila.length)
    for (const h of hijosDe(pila.pop()!)) if (!fuera.has(h.id)) (fuera.add(h.id), pila.push(h.id));
  return fuera;
}

/**
 * «Comercial CDS» → «COMERCIAL-CDS»: el código que pide el servidor, sacado del
 * nombre. Se ve en el selector de equipo de arriba, así que va limpio; si ya
 * hay uno igual, «-2», «-3»…
 */
export function codigoDesde(nombre: string, usados: Iterable<string> = []): string {
  const base =
    nombre
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 24) || 'EQUIPO';
  const ocupados = new Set(usados);
  if (!ocupados.has(base)) return base;
  let n = 2;
  while (ocupados.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

/** Mi empresa › Equipos (maqueta de Melanie, 25-oct): el organigrama. */
export function EquiposSection() {
  const organigrama = useCarga(() => miEmpresaService.organigrama(), []);
  const personas = useCarga(() => miEmpresaService.personas(), []);
  const [abierto, setAbierto] = useState<EquipoDelOrganigrama | 'nuevo' | null>(null);
  // Soporte sólo mira en Mi empresa (jerarquía y equipos los frena el servidor).
  const enSoporte = useEnSoporte();

  const equipos = organigrama.datos ?? [];
  const hijosDe = useMemo(() => armarArbol(equipos), [equipos]);
  const raices = hijosDe(null);

  return (
    <MarcoDeMiEmpresa
      seccion="equipos"
      acciones={
        <Button
          variant="degradado"
          size="lg"
          className="px-6"
          onClick={() => setAbierto('nuevo')}
          {...atributosSoporteNoPuede(enSoporte)}
        >
          <Plus className="size-4" aria-hidden="true" />
          Nuevo equipo
        </Button>
      }
    >
      {organigrama.estado === 'error' && !organigrama.datos ? (
        <EstadoError onReintentar={organigrama.recargar}>
          No pudimos cargar los equipos.
        </EstadoError>
      ) : !organigrama.datos ? (
        <Skeleton className="h-96 w-full rounded-tarjeta" />
      ) : raices.length === 0 ? (
        <EstadoVacio icono={Network} titulo="Todavía no hay equipos">
          Arma el organigrama con «Nuevo equipo»: primero la dirección y después las áreas que
          dependen de ella.
        </EstadoVacio>
      ) : (
        <section
          aria-label="Organigrama"
          className="overflow-x-auto rounded-tarjeta border bg-lienzo/60 px-6 py-9 shadow-card"
        >
          <ul className="flex min-w-max justify-center">
            {raices.map((e) => (
              <Rama
                key={e.id}
                equipo={e}
                hijosDe={hijosDe}
                raiz
                enRaiz={raices.length}
                onAbrir={setAbierto}
              />
            ))}
          </ul>
          <p className="mt-8 text-center text-sm text-muted-foreground">
            Toca un equipo para cambiar su líder, de quién depende o quiénes lo forman.
          </p>
        </section>
      )}

      {abierto && (
        <DialogoDeEquipo
          equipo={abierto === 'nuevo' ? null : abierto}
          equipos={equipos}
          hijosDe={hijosDe}
          personas={personas.datos ?? []}
          onCerrar={() => setAbierto(null)}
          onListo={() => {
            setAbierto(null);
            organigrama.recargar();
          }}
        />
      )}
    </MarcoDeMiEmpresa>
  );
}

/** Un equipo y lo que cuelga de él, con las líneas del organigrama. */
function Rama({
  equipo,
  hijosDe,
  raiz = false,
  enRaiz = 1,
  onAbrir,
}: {
  equipo: EquipoDelOrganigrama;
  hijosDe: (id: string | null) => EquipoDelOrganigrama[];
  raiz?: boolean;
  enRaiz?: number;
  onAbrir: (e: EquipoDelOrganigrama) => void;
}) {
  const hijos = hijosDe(equipo.id);
  const gente = genteDelEquipo(equipo);
  return (
    <li
      className={cn(
        'relative flex flex-col items-center px-3',
        // La línea que baja del padre y el tramo horizontal que une a los hermanos.
        !raiz &&
          'pt-8 before:absolute before:left-1/2 before:top-0 before:h-8 before:w-px before:bg-border after:absolute after:left-0 after:right-0 after:top-0 after:h-px after:bg-border first:after:left-1/2 last:after:right-1/2 only:after:hidden'
      )}
    >
      <button
        type="button"
        onClick={() => onAbrir(equipo)}
        className={cn(
          'flex w-64 flex-col items-center gap-1.5 rounded-2xl border bg-card px-5 py-5 text-center shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          raiz && enRaiz === 1 && 'border-2 border-primary'
        )}
      >
        <span className="text-[17px] font-semibold">{equipo.nombre}</span>
        <span className="text-sm text-muted-foreground">{subtituloDelEquipo(equipo)}</span>
        <span className="mt-2 flex min-h-10 items-center" aria-label={`${gente.length} personas`}>
          {gente.length === 0 ? (
            <span className="text-xs text-muted-foreground">Sin personas todavía</span>
          ) : (
            <>
              {gente.slice(0, 4).map((p, i) => (
                <AvatarDePersona
                  key={p.id}
                  nombre={nombreCompleto(p)}
                  clave={p.id}
                  className={cn('size-10 ring-2 ring-card', i > 0 && '-ml-2.5')}
                />
              ))}
              {gente.length > 4 && (
                <span className="-ml-2.5 grid size-10 place-items-center rounded-full bg-muted text-xs font-semibold ring-2 ring-card">
                  +{gente.length - 4}
                </span>
              )}
            </>
          )}
        </span>
      </button>
      {hijos.length > 0 && (
        <ul className="relative flex pt-8 before:absolute before:left-1/2 before:top-0 before:h-8 before:w-px before:bg-border">
          {hijos.map((h) => (
            <Rama key={h.id} equipo={h} hijosDe={hijosDe} onAbrir={onAbrir} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** Crear un equipo, o cambiarle el nombre, el superior, el líder y la gente. */
function DialogoDeEquipo({
  equipo,
  equipos,
  hijosDe,
  personas,
  onCerrar,
  onListo,
}: {
  equipo: EquipoDelOrganigrama | null;
  equipos: EquipoDelOrganigrama[];
  hijosDe: (id: string | null) => EquipoDelOrganigrama[];
  personas: PersonaDeLaEmpresa[];
  onCerrar: () => void;
  onListo: () => void;
}) {
  const [nombre, setNombre] = useState(equipo?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(equipo?.descripcion ?? '');
  const [superior, setSuperior] = useState(
    equipo?.parentId ?? (equipo ? '' : (hijosDe(null)[0]?.id ?? ''))
  );
  const [lider, setLider] = useState(equipo?.lider?.id ?? '');
  const [miembros, setMiembros] = useState<string[]>(equipo?.miembros.map((m) => m.id) ?? []);
  const [sumar, setSumar] = useState('');
  const [guardando, setGuardando] = useState(false);
  const enSoporte = useEnSoporte();
  // Líneas de negocio: las de la empresa y las que ya tiene este equipo.
  const lineas = useCarga(() => masterDataService.getLOBs(), []);
  const lineasDelEquipo = useCarga(
    () => (equipo ? organizationService.listLobs(equipo.id) : Promise.resolve([])),
    [equipo?.id]
  );
  const [lineasElegidas, setLineasElegidas] = useState<Set<string> | null>(null);
  const [buscaLinea, setBuscaLinea] = useState('');
  const elegidas = lineasElegidas ?? new Set((lineasDelEquipo.datos ?? []).map((l) => l.id));

  const activas = personas.filter((p) => p.estado !== 'INACTIVO');
  const fuera = equipo ? descendientes(equipo.id, hijosDe) : new Set<string>();
  const superiores = equipos.filter((e) => !fuera.has(e.id));
  const porId = new Map(activas.map((p) => [p.id, p]));
  const nombreDe = (id: string) => {
    const p = porId.get(id) ?? equipo?.miembros.find((m) => m.id === id);
    if (!p) return 'Persona';
    return 'nombre' in p ? `${p.nombre} ${p.apellido}`.trim() : nombreCompleto(p);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      let id = equipo?.id;
      if (equipo) {
        if (nombre.trim() !== equipo.nombre || (descripcion.trim() || null) !== equipo.descripcion)
          await organizationService.update(equipo.id, {
            name: nombre.trim(),
            description: descripcion.trim() || null,
          });
      } else {
        // Un equipo borrado conserva su código: si el servidor dice que está tomado, se prueba el siguiente.
        const usados = equipos.map((e) => e.codigo);
        for (let intento = 0; !id && intento < 5; intento++) {
          const code = codigoDesde(nombre, usados);
          try {
            const creado = await organizationService.create({
              name: nombre.trim(),
              code,
              description: descripcion.trim() || null,
            });
            id = creado.id;
          } catch (e: any) {
            if (e?.response?.status !== 409) throw e;
            usados.push(code);
          }
        }
        if (!id) throw new Error('No encontramos un código libre para el equipo.');
      }
      const parentId = superior || null;
      const leaderId = lider || null;
      if (!equipo || parentId !== equipo.parentId || leaderId !== (equipo.lider?.id ?? null))
        await miEmpresaService.jerarquia(id!, { parentId, leaderId });

      // El líder también es parte del equipo: así ve las cotizaciones de su gente.
      const quedan = new Set([...miembros, ...(leaderId ? [leaderId] : [])]);
      const estaban = new Set(equipo?.miembros.map((m) => m.id) ?? []);
      for (const u of quedan)
        if (!estaban.has(u)) await organizationService.addMember(id!, { userId: u });
      for (const u of estaban) if (!quedan.has(u)) await organizationService.removeMember(id!, u);

      // Sólo si se tocaron las líneas: el servidor reemplaza la lista entera.
      if (lineasElegidas) await organizationService.setLobs(id!, Array.from(lineasElegidas));

      toast({
        title: equipo ? 'Equipo actualizado' : `Equipo «${nombre.trim()}» creado`,
        variant: 'success',
      });
      onListo();
    } catch (e: any) {
      toast({
        title: 'No se pudo guardar el equipo',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    if (!equipo) return;
    try {
      // Los que dependían de él suben a su superior: el organigrama no se corta.
      for (const h of hijosDe(equipo.id))
        await miEmpresaService.jerarquia(h.id, { parentId: equipo.parentId });
      await organizationService.delete(equipo.id);
      toast({ title: `Equipo «${equipo.nombre}» borrado` });
      onListo();
    } catch (e: any) {
      toast({
        title: 'No se pudo borrar',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{equipo ? equipo.nombre : 'Nuevo equipo'}</DialogTitle>
          <DialogDescription>
            {equipo
              ? 'De quién depende, quién lo lidera y quiénes lo forman.'
              : 'Un área de tu empresa: la dirección, una gerencia, un equipo de ventas.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (nombre.trim()) void guardar();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="equipo-nombre">Nombre</Label>
              <Input
                id="equipo-nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej.: Sucursal Equipetrol"
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="equipo-descripcion">Qué hace</Label>
              <Input
                id="equipo-descripcion"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ej.: Dermocosmética"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="equipo-superior">Depende de</Label>
              <SelectNativo
                id="equipo-superior"
                value={superior}
                onChange={(e) => setSuperior(e.target.value)}
              >
                <option value="">Nadie (es la cabeza)</option>
                {superiores.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nombre}
                  </option>
                ))}
              </SelectNativo>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="equipo-lider">Líder</Label>
              <SelectNativo
                id="equipo-lider"
                value={lider}
                onChange={(e) => setLider(e.target.value)}
              >
                <option value="">Sin líder</option>
                {activas.map((p) => (
                  <option key={p.id} value={p.id}>
                    {`${p.nombre} ${p.apellido}`.trim()}
                  </option>
                ))}
              </SelectNativo>
            </div>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sm font-medium">Personas del equipo</legend>
            <ul className="flex flex-wrap gap-2">
              {miembros.length === 0 && (
                <li className="text-sm text-muted-foreground">Nadie todavía.</li>
              )}
              {miembros.map((id) => (
                <li
                  key={id}
                  className="flex items-center gap-2 rounded-full border bg-muted/50 py-1 pl-1 pr-2 text-sm"
                >
                  <AvatarDePersona
                    nombre={nombreDe(id)}
                    clave={id}
                    className="size-6 text-[10px]"
                  />
                  {nombreDe(id)}
                  {id === lider && <span className="text-xs text-muted-foreground">· líder</span>}
                  <button
                    type="button"
                    aria-label={`Sacar a ${nombreDe(id)}`}
                    onClick={() => setMiembros((m) => m.filter((x) => x !== id))}
                    className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <SelectNativo
                aria-label="Sumar a una persona"
                value={sumar}
                onChange={(e) => setSumar(e.target.value)}
                className="flex-1"
              >
                <option value="">Sumar a alguien…</option>
                {activas
                  .filter((p) => !miembros.includes(p.id))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {`${p.nombre} ${p.apellido}`.trim()} · {p.cargo}
                    </option>
                  ))}
              </SelectNativo>
              <Button
                type="button"
                variant="outline"
                disabled={!sumar}
                onClick={() => {
                  setMiembros((m) => [...m, sumar]);
                  setSumar('');
                }}
              >
                Sumar
              </Button>
            </div>
          </fieldset>

          <LineasDeNegocio
            lineas={(lineas.datos ?? []).filter((l) => l.isActive !== false)}
            cargando={lineas.estado === 'cargando' || lineasDelEquipo.estado === 'cargando'}
            error={lineas.estado === 'error' || lineasDelEquipo.estado === 'error'}
            elegidas={elegidas}
            busca={buscaLinea}
            onBusca={setBuscaLinea}
            onCambiar={setLineasElegidas}
          />

          <DialogFooter className="flex-wrap gap-2 sm:justify-between">
            {equipo ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive"
                onClick={borrar}
                {...atributosSoporteNoPuede(enSoporte)}
              >
                Borrar equipo
              </Button>
            ) : (
              <span />
            )}
            <span className="flex gap-2">
              <Button type="button" variant="outline" onClick={onCerrar}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="degradado"
                disabled={!nombre.trim() || guardando || enSoporte}
                title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : undefined}
                isLoading={guardando}
              >
                {equipo ? 'Guardar' : 'Crear equipo'}
              </Button>
            </span>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type LineaDeNegocio = { id: string; name: string; code: string };

/** Las líneas de negocio de un equipo: se ven y se marcan o desmarcan, igual que en la pantalla vieja. */
function LineasDeNegocio({
  lineas,
  cargando,
  error,
  elegidas,
  busca,
  onBusca,
  onCambiar,
}: {
  lineas: LineaDeNegocio[];
  cargando: boolean;
  error: boolean;
  elegidas: Set<string>;
  busca: string;
  onBusca: (v: string) => void;
  onCambiar: (s: Set<string>) => void;
}) {
  const q = busca.trim().toLowerCase();
  const visibles = q
    ? lineas.filter((l) => `${l.name} ${l.code}`.toLowerCase().includes(q))
    : lineas;
  const alternar = (id: string) => {
    const s = new Set(elegidas);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    onCambiar(s);
  };
  return (
    <fieldset className="flex flex-col gap-2" aria-label="Líneas de negocio">
      <legend className="mb-1 text-sm font-medium">
        Líneas de negocio
        {!cargando && !error && ` (${lineas.filter((l) => elegidas.has(l.id)).length})`}
      </legend>
      <p className="text-xs text-muted-foreground">
        De qué líneas de negocio se ocupa este equipo. Sirve para tenerlo anotado: no reparte
        accesos por sí solo.
      </p>
      {error ? (
        <p className="text-sm text-destructive">No pudimos cargar las líneas de negocio.</p>
      ) : cargando ? (
        <Skeleton className="h-20 w-full" />
      ) : lineas.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Todavía no hay líneas de negocio cargadas en tu empresa.
        </p>
      ) : (
        <>
          <Input
            aria-label="Buscar línea de negocio"
            value={busca}
            onChange={(e) => onBusca(e.target.value)}
            placeholder="Buscar por nombre o código…"
          />
          <ul className="grid max-h-44 gap-1.5 overflow-y-auto sm:grid-cols-2">
            {visibles.map((l) => (
              <li key={l.id}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm',
                    elegidas.has(l.id) ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={elegidas.has(l.id)}
                    onChange={() => alternar(l.id)}
                  />
                  <span className="min-w-0 flex-1 truncate">{l.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
    </fieldset>
  );
}
